'use client';

import * as React from 'react';
import { motion } from 'motion/react';
import { cn } from '../../lib/utils';

interface AnimatedTitleProps {
  className?: string;
}

const sequences = [
  '{> Accelerate',
  '{> WP',
  '{> Creation',
  '{> Accelerate Wp Creation'
];

export function AnimatedTitle({ className }: AnimatedTitleProps) {
  const [currentSequence, setCurrentSequence] = React.useState(0);
  const [displayText, setDisplayText] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [charIndex, setCharIndex] = React.useState(0);

  React.useEffect(() => {
    const currentText = sequences[currentSequence];
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseTime = isDeleting ? 500 : 2000;

    const timer = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (charIndex < currentText.length) {
          setDisplayText(currentText.substring(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } else {
          // Finished typing, wait then start deleting
          setTimeout(() => {
            setIsDeleting(true);
          }, pauseTime);
        }
      } else {
        // Deleting
        if (charIndex > 0) {
          setDisplayText(currentText.substring(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        } else {
          // Finished deleting, move to next sequence
          setIsDeleting(false);
          setCurrentSequence((prev) => (prev + 1) % sequences.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, currentSequence]);

  return (
    <motion.div
      className={cn('flex items-center', className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <span className={cn(
        "text-sm sm:text-lg md:text-xl font-bold font-ubuntu dark:text-glow",
        className?.includes('text-white') ? 'text-white' : 'text-gray-900 dark:text-white'
      )}>
        {displayText}
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
          className="inline-block w-0.5 h-3 sm:h-4 md:h-5 bg-gradient-to-b from-[#c780ff] to-[#8a2be2] ml-1"
        />
      </span>
    </motion.div>
  );
}
